/**
 * 2048 PK 竞技场全局 DOM 引用绑定与 HTML 转义工具
 * 集中管理顶栏 HUD、三栏横屏战场、双模式入口大厅、3v3 席位大厅与终局战报的 DOM 节点引用
 */

export function escapeHtml(str: string): string {
  return str.replace(
    /[&<>"']/g,
    (m) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[m] || m
  );
}

export interface ArenaDomRefs {
  // 顶栏 HUD 与控制按钮
  elHudModePill: HTMLElement;
  elHudRoomCode: HTMLElement;
  elHudRedScore: HTMLElement;
  elHudBlueScore: HTMLElement;
  elHudTeamTugRed: HTMLElement;
  elHudMatchTimer: HTMLElement;
  elHudNetText: HTMLElement;
  elHudNetDot: HTMLElement;
  elBtnCopyLink: HTMLElement;
  elBtnSoundToggle: HTMLElement;
  elSoundIconOn: HTMLElement;
  elSoundIconOff: HTMLElement;
  elBtnDpadToggle: HTMLElement;
  elVirtualDpad: HTMLElement;
  elBtnExitGame: HTMLElement;

  // 左栏友方微缩盘 & 局内聊天室 & 右栏敌方微缩盘容器
  elAlliesMiniContainer: HTMLElement;
  elEnemiesMiniContainer: HTMLElement;
  elExtraEnemiesSlot: HTMLElement;
  elPrimaryEnemyCard: HTMLElement;
  elPrimaryEnemyName: HTMLElement;
  elBattleChatList: HTMLElement;
  elBattleChatInput: HTMLInputElement;
  elBtnBattleChatSend: HTMLElement;

  // 中央主棋盘 (本地选手 / 观战主视角)
  elLocalTeamTag: HTMLElement;
  elLocalName: HTMLElement;
  elLocalRole: HTMLElement;
  elLocalDmcLetter: HTMLElement;
  elLocalDmcGaugeBar: HTMLElement;
  elLocalScore: HTMLElement;
  elLocalChargeFill: HTMLElement;
  elLocalChargeStatus: HTMLElement;
  elLocalRescueStatus: HTMLElement;
  elLocalBoardWrapper: HTMLElement;
  elLocalTileContainer: HTMLElement;
  elLocalGridCells: HTMLElement[];

  // 右栏主锁定敌盘
  elRivalTeamTag: HTMLElement;
  elRivalName: HTMLElement;
  elRivalRole: HTMLElement;
  elRivalDmcLetter: HTMLElement;
  elRivalDmcGaugeBar: HTMLElement;
  elRivalScore: HTMLElement;
  elRivalMirrorWrapper: HTMLElement;
  elRivalTileContainer: HTMLElement;
  elRivalGridCells: HTMLElement[];
  elRivalStatusText: HTMLElement;

  // 战况播报与全屏粒子特效层
  elCombatLog: HTMLElement;
  elEffectOverlay: HTMLElement;
  elPkChaseBanner: HTMLElement;
  elPkChaseText: HTMLElement;
  elLoadingOverlay: HTMLElement;
  elLoadingText: HTMLElement;
  elBtnJoinText: HTMLElement;

  // 阶段 1：三模式入口大厅 DOM
  elModalLobby: HTMLElement;
  elInputPlayerName: HTMLInputElement;
  elBtnPortalSpeed: HTMLElement;
  elBtnPortal2048: HTMLElement;
  elBtnPortalEndless: HTMLElement;
  elInputRoomCode: HTMLInputElement;
  elBtnJoinRoom: HTMLElement;
  elBtnRefreshRooms: HTMLElement;
  elBtnToggleJoinDrawer: HTMLElement;
  elJoinDrawerRow: HTMLElement;
  elBtnCancelJoin: HTMLElement;
  elLobbyRoomsList: HTMLElement;
  elRoomsCountBadge: HTMLElement;

  // 阶段 2：3v3 席位大厅与聊天室 DOM
  elModalSeatLobby: HTMLElement;
  elBtnSeatBack: HTMLElement;
  elSeatRoomCode: HTMLElement;
  elBtnSeatCopyLink: HTMLElement;
  elSeatFormatPill: HTMLElement;
  elTabModeSpeed: HTMLElement;
  elTabMode2048: HTMLElement;
  elTabModeEndless: HTMLElement;
  elRedTeamCount: HTMLElement;
  elBlueTeamCount: HTMLElement;
  elRedSeatsContainer: HTMLElement;
  elBlueSeatsContainer: HTMLElement;
  elSeatSpectatorList: HTMLElement;
  elBtnJoinSpectator: HTMLElement;
  elSeatChatList: HTMLElement;
  elSeatChatInput: HTMLInputElement;
  elBtnSeatChatSend: HTMLElement;
  elSeatCountdownBanner: HTMLElement;
  elSeatCountdownNum: HTMLElement;
  elBtnSeatStart: HTMLButtonElement;
  elBtnSeatStartText: HTMLElement;
  elBtnSeatCancel: HTMLElement;

  // 阶段 4：终局结算 DOM
  elModalGameover: HTMLElement;
  elGameoverCard: HTMLElement;
  elGameoverModeTag: HTMLElement;
  elGameoverBanner: HTMLElement;
  elGameoverReason: HTMLElement;
  elStatMyScore: HTMLElement;
  elStatRivalScore: HTMLElement;
  elGameoverScoreDiff: HTMLElement;
  elGameoverVsFillMy: HTMLElement;
  elStatMyMax: HTMLElement;
  elStatRivalMax: HTMLElement;
  elStatMyRank: HTMLElement;
  elStatSlashHits: HTMLElement;
  elStatScoreDrained: HTMLElement;
  elStatShieldBlocks: HTMLElement;
  elSettlementFocusTeamDot: HTMLElement;
  elSettlementFocusName: HTMLElement;
  elSettlementFocusBadges: HTMLElement;
  elSettlementFocusRating: HTMLElement;
  elSettlementFocusScore: HTMLElement;
  elSettlementSnapshotTiles: HTMLElement;
  elSettlementScoreboardBody: HTMLElement;
  elSettlementMvpCallout: HTMLElement;
  elBtnInspectBoard: HTMLElement;
  elBtnRestoreReport: HTMLElement;
  elBtnInspectRematch: HTMLElement;
  elBtnRematch: HTMLElement;
  elBtnBackLobby: HTMLElement;
}

export function bindArenaDom(container: HTMLElement): ArenaDomRefs {
  const q = <T extends HTMLElement>(sel: string) => container.querySelector(sel) as T;

  return {
    elHudModePill: q("#hud-mode-pill"),
    elHudRoomCode: q("#hud-room-code"),
    elHudRedScore: q("#hud-red-score"),
    elHudBlueScore: q("#hud-blue-score"),
    elHudTeamTugRed: q("#hud-team-tug-red"),
    elHudMatchTimer: q("#hud-match-timer"),
    elHudNetText: q("#hud-net-text"),
    elHudNetDot: q(".net-dot"),
    elBtnCopyLink: q("#btn-copy-link"),
    elBtnSoundToggle: q("#btn-sound-toggle"),
    elSoundIconOn: q(".icon-sound-on"),
    elSoundIconOff: q(".icon-sound-off"),
    elBtnDpadToggle: q("#btn-dpad-toggle"),
    elVirtualDpad: q("#virtual-dpad"),
    elBtnExitGame: q("#btn-exit-game"),

    elAlliesMiniContainer: q("#allies-mini-container"),
    elEnemiesMiniContainer: q("#enemies-mini-container"),
    elExtraEnemiesSlot: q("#extra-enemies-slot"),
    elPrimaryEnemyCard: q("#primary-enemy-card"),
    elPrimaryEnemyName: q("#primary-enemy-name"),

    elBattleChatList: q("#battle-chat-list"),
    elBattleChatInput: q("#battle-chat-input"),
    elBtnBattleChatSend: q("#btn-battle-chat-send"),

    elLocalTeamTag: q("#local-team-tag"),
    elLocalName: q("#local-player-name"),
    elLocalRole: q("#local-role-badge"),
    elLocalDmcLetter: q("#local-dmc-letter"),
    elLocalDmcGaugeBar: q("#local-dmc-gauge-bar"),
    elLocalScore: q("#local-score"),
    elLocalChargeFill: q("#local-charge-fill"),
    elLocalChargeStatus: q("#local-attack-status"),
    elLocalRescueStatus: q("#local-rescue-status"),
    elLocalBoardWrapper: q("#local-board-wrapper"),
    elLocalTileContainer: q("#local-tile-container"),
    elLocalGridCells: Array.from(container.querySelectorAll("#local-grid-container .grid-cell")),

    elRivalTeamTag: q("#rival-team-tag"),
    elRivalName: q("#rival-player-name"),
    elRivalRole: q("#rival-role-badge"),
    elRivalDmcLetter: q("#rival-dmc-letter"),
    elRivalDmcGaugeBar: q("#rival-dmc-gauge-bar"),
    elRivalScore: q("#rival-score"),
    elRivalMirrorWrapper: q("#rival-mirror-wrapper"),
    elRivalTileContainer: q("#rival-tile-container"),
    elRivalGridCells: Array.from(container.querySelectorAll("#rival-mirror-wrapper .rival-grid-cell")),
    elRivalStatusText: q("#rival-status-text"),

    elCombatLog: q("#pk-combat-log"),
    elEffectOverlay: q("#pk-effect-overlay"),
    elPkChaseBanner: q("#pk-chase-banner"),
    elPkChaseText: q("#pk-chase-text"),

    elLoadingOverlay: q("#pk-loading-overlay"),
    elLoadingText: q("#loading-status-text"),
    elBtnJoinText: q("#btn-join-text"),

    elModalLobby: q("#modal-lobby"),
    elInputPlayerName: q("#input-player-name"),
    elBtnPortalSpeed: q("#btn-portal-speed"),
    elBtnPortal2048: q("#btn-portal-2048"),
    elBtnPortalEndless: q("#btn-portal-endless"),
    elInputRoomCode: q("#input-room-code"),
    elBtnJoinRoom: q("#btn-join-room"),
    elBtnRefreshRooms: q("#btn-refresh-rooms"),
    elBtnToggleJoinDrawer: q("#btn-toggle-join-drawer"),
    elJoinDrawerRow: q("#join-drawer-row"),
    elBtnCancelJoin: q("#btn-cancel-join"),
    elLobbyRoomsList: q("#lobby-rooms-list"),
    elRoomsCountBadge: q("#rooms-count-badge"),

    elModalSeatLobby: q("#modal-seat-lobby"),
    elBtnSeatBack: q("#btn-seat-back"),
    elSeatRoomCode: q("#seat-room-code"),
    elBtnSeatCopyLink: q("#btn-seat-copy-link"),
    elSeatFormatPill: q("#seat-format-pill"),
    elTabModeSpeed: q("#tab-mode-speed"),
    elTabMode2048: q("#tab-mode-2048"),
    elTabModeEndless: q("#tab-mode-endless"),
    elRedTeamCount: q("#red-team-count"),
    elBlueTeamCount: q("#blue-team-count"),
    elRedSeatsContainer: q("#red-seats-container"),
    elBlueSeatsContainer: q("#blue-seats-container"),
    elSeatSpectatorList: q("#seat-spectator-list"),
    elBtnJoinSpectator: q("#btn-join-spectator"),
    elSeatChatList: q("#seat-chat-list"),
    elSeatChatInput: q("#seat-chat-input"),
    elBtnSeatChatSend: q("#btn-seat-chat-send"),
    elSeatCountdownBanner: q("#seat-countdown-banner"),
    elSeatCountdownNum: q("#seat-countdown-num"),
    elBtnSeatStart: q("#btn-seat-start"),
    elBtnSeatStartText: q("#btn-seat-start-text"),
    elBtnSeatCancel: q("#btn-seat-cancel"),

    elModalGameover: q("#modal-gameover"),
    elGameoverCard: q("#gameover-card"),
    elGameoverModeTag: q("#gameover-mode-tag"),
    elGameoverBanner: q("#gameover-banner"),
    elGameoverReason: q("#gameover-reason"),
    elStatMyScore: q("#stat-my-score"),
    elStatRivalScore: q("#stat-rival-score"),
    elGameoverScoreDiff: q("#gameover-score-diff"),
    elGameoverVsFillMy: q("#gameover-vs-fill-my"),
    elStatMyMax: q("#stat-my-max"),
    elStatRivalMax: q("#stat-rival-max"),
    elStatMyRank: q("#stat-my-rank"),
    elStatSlashHits: q("#stat-slash-hits"),
    elStatScoreDrained: q("#stat-score-drained"),
    elStatShieldBlocks: q("#stat-shield-blocks"),
    elSettlementFocusTeamDot: q("#settlement-focus-team-dot"),
    elSettlementFocusName: q("#settlement-focus-name"),
    elSettlementFocusBadges: q("#settlement-focus-badges"),
    elSettlementFocusRating: q("#settlement-focus-rating"),
    elSettlementFocusScore: q("#settlement-focus-score"),
    elSettlementSnapshotTiles: q("#settlement-snapshot-tiles"),
    elSettlementScoreboardBody: q("#settlement-scoreboard-body"),
    elSettlementMvpCallout: q("#settlement-mvp-callout"),
    elBtnInspectBoard: q("#btn-inspect-board"),
    elBtnRestoreReport: q("#btn-restore-report"),
    elBtnInspectRematch: q("#btn-inspect-rematch"),
    elBtnRematch: q("#btn-rematch"),
    elBtnBackLobby: q("#btn-back-lobby"),
  };
}

